import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Laptop, Globe2, Waves } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";
import { Chapter, SubTitle, Body } from "@/components/Chapter";
import { FinTable } from "@/components/FinTable";
import { RoiBarChart } from "@/components/Charts";

const SCENARIO_KEYS = ["conservador", "base", "optimista"];
const PERFIL_ICONS = [Laptop, Globe2, Waves];

const SensTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-sm border border-gold/50 bg-ivory px-4 py-3 shadow-[0_8px_28px_rgba(26,36,32,0.15)]">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-graphite/55">Ocupación {label}</p>
      <p className="mt-1 font-serif text-xl font-semibold text-jade">
        {String(payload[0].value).replace(".", ",")}%
      </p>
    </div>
  );
};

const SensChart = ({ data }) => (
  <div data-testid="sensibilidad-chart" className="rounded-sm border border-sand bg-white/85 p-5 shadow-[0_4px_24px_rgba(31,110,90,0.05)] sm:p-7">
    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-graphite/50">
      ROI Cash según nivel de ocupación
    </p>
    <div className="mt-4 h-64 sm:h-72">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 10, right: 8, left: -14, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E4DDCC" vertical={false} />
          <XAxis dataKey="name" tick={{ fill: "#1A1A1A", fontSize: 11, fontFamily: "Manrope" }} axisLine={{ stroke: "#E4DDCC" }} tickLine={false} />
          <YAxis tick={{ fill: "#1A1A1A", fontSize: 11, opacity: 0.6 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
          <Tooltip cursor={{ fill: "rgba(203,161,83,0.08)" }} content={<SensTooltip />} />
          <Bar dataKey="roi" radius={[3, 3, 0, 0]} maxBarSize={52}>
            {data.map((e, i) => (
              <Cell key={i} fill={e.name === "70%" ? "#CBA153" : "#2C5446"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
);

export const Rentabilidad = ({ d }) => {
  const [sc, setSc] = useState("base");
  const scenario = d.escenarios[sc];

  return (
    <>
      <Chapter id="09" num="09" title="Escenarios de Rentabilidad">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Escenarios">
          {SCENARIO_KEYS.map((k) => (
            <button
              key={k}
              data-testid={`scenario-tab-${k}`}
              role="tab"
              aria-selected={sc === k}
              onClick={() => setSc(k)}
              className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] transition-all duration-300 ${
                sc === k
                  ? "bg-jade text-ivory"
                  : "border border-sand bg-white/70 text-graphite/60 hover:border-gold hover:text-jade"
              }`}
            >
              {d.escenarios[k].nombre}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={sc}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
            className="space-y-8"
          >
            <div className="grid grid-cols-3 gap-3">
              {[["ROI Cash", scenario.stats.roi], ["Margen neto", scenario.stats.margen], ["Break-even", scenario.stats.breakeven]].map(
                ([label, value], i) => (
                  <div key={label} data-testid={`scenario-stat-${i}`} className="rounded-sm border border-gold/40 bg-gold/[0.08] px-4 py-4 text-center sm:px-6">
                    <p className="font-serif text-xl font-semibold text-jade sm:text-3xl">{value}</p>
                    <p className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-graphite/55 sm:text-[10px]">{label}</p>
                  </div>
                )
              )}
            </div>
            <FinTable
              testid={`tabla-escenario-${sc}`}
              columns={["Concepto", "Valor"]}
              rows={scenario.rows}
              note={d.escenariosNota}
            />
          </motion.div>
        </AnimatePresence>

        <RoiBarChart data={d.roiSeries} />
      </Chapter>

      <Chapter id="10" num="10" title="Análisis de Sensibilidad">
        <Body>{d.sensibilidad.intro}</Body>
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
          <FinTable
            testid="tabla-sensibilidad"
            columns={["Ocupación", "Ingresos", "Beneficio neto", "ROI Cash"]}
            rows={d.sensibilidad.rows}
            rightFrom={1}
          />
          <SensChart data={d.sensSeries} />
        </div>
        <Body className="italic text-graphite/55">{d.sensibilidad.cierre}</Body>
      </Chapter>

      <Chapter id="11" num="11" title="Alquiler a Largo Plazo">
        <div>
          <SubTitle>Perfil del inquilino</SubTitle>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {d.largoPlazo.perfil.map((p, i) => {
              const Icon = PERFIL_ICONS[i % PERFIL_ICONS.length];
              return (
                <div key={p.titulo} data-testid={`perfil-card-${i}`} className="rounded-sm border border-sand bg-white/85 p-6 transition-colors duration-300 hover:border-gold/60">
                  <Icon className="h-5 w-5 text-gold" />
                  <p className="mt-3 font-serif text-lg leading-snug text-jade">{p.titulo}</p>
                  <p className="mt-2 text-xs leading-relaxed text-graphite/65">{p.texto}</p>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <SubTitle>Por qué es interesante</SubTitle>
          <Body className="mt-3">{d.largoPlazo.porque}</Body>
        </div>
        <FinTable
          testid="tabla-largo-plazo"
          columns={["Concepto", "Valor"]}
          rows={d.largoPlazo.tabla}
        />
      </Chapter>

      <Chapter id="12" num="12" title="Canales de Comercialización del Alquiler">
        <Body>{d.canales.intro}</Body>
        <FinTable
          testid="tabla-canales"
          columns={["Canal", "Comisión", "% estimado de reservas"]}
          rows={d.canales.rows}
          rightFrom={1}
        />
        <div>
          <SubTitle>{d.canales.escenarioTitulo}</SubTitle>
          <div className="mt-5">
            <FinTable
              testid="tabla-canales-escenario"
              columns={["Canal", "% reservas", "Tarifa publicada media", "Ingresos netos anuales", "Comisión anual estimada"]}
              rows={d.canales.escenario}
              rightFrom={1}
              note={d.canales.nota}
            />
          </div>
        </div>
      </Chapter>
    </>
  );
};
